import { useCallback, useContext } from 'react';
import createDataContext from '../services/createDataContext';

import {
	getProgramCoordinationOptions,
	getProgramStatuses,
	getProgramTypes,
	getTemplateModules,
	getTemplateOccurrences,
	getTemplateRequirements,
	getTemplateStatuses,
	getTemplateTypes
} from '../api/nora/lookups';
import { getTemplateModuleTags } from '../api/nora/programs';

import programsReducer, {
	RESET_SUBMISSIONS_TABLE_STATE,
	SET_COORDINATION,
	SET_COORDINATION_OPTIONS,
	SET_PARTICIPANTS,
	SET_PROGRAM,
	SET_PROGRAM_LIST,
	SET_PROGRAM_STATUSES,
	SET_PROGRAM_TYPES,
	SET_SUBMISSIONS,
	SET_SUBMISSIONS_TABLE_STATE,
	SET_TABLE_STATE,
	SET_TEMPLATE,
	SET_TEMPLATES,
	SET_TEMPLATE_MODULES,
	SET_TEMPLATE_MODULE_FILTERS,
	SET_TEMPLATE_MODULE_TAGS,
	SET_TEMPLATE_OCCURRENCES,
	SET_TEMPLATE_REQUIREMENTS,
	SET_TEMPLATE_TYPES,
	SET_TEMPLATE_STATUSES
} from '../reducers/programsReducer';

export let controller = new AbortController();

const initialState = {
	coordinationOptions: [],
	override: {},
	program: null,
	programList: [],
	programStatuses: [],
	programTypes: [],
	submissions: [],
	submissionsTableState: {
		filters: [],
		pageIndex: 0,
		pageSize: 10,
		rows: [],
		selectedIds: [],
		sortBy: []
	},
	tableState: {
		filters: [],
		pageIndex: 0,
		pageSize: 10,
		rows: [],
		selectedIds: [],
		sortBy: []
	},
	template: null,
	templates: [],
	templateModules: [],
	templateModuleFilters: [],
	templateModuleTags: [],
	templateOccurrences: [],
	templateStatuses: [],
	templateTypes: [],
	templateRequirements: []
};

const setProgramStatuses = async dispatch => {
	try {
		const [ { types } ] = await getProgramStatuses( controller.signal );

		const mappedStatuses = types.map( ( { id, name } ) => ( {
			key: id,
			text: name,
			value: id
		} ) );

		SET_PROGRAM_STATUSES( dispatch, controller.signal )( mappedStatuses );
	} catch ( error ) {
		// handle
	}
};

const setProgramTypes = async dispatch => {
	try {
		const [ { types } ] = await getProgramTypes( controller.signal );

		const mappedTypes = types.map( ( { id, name } ) => ( {
			key: id,
			text: name,
			value: id
		} ) );

		SET_PROGRAM_TYPES( dispatch, controller.signal )( mappedTypes );
	} catch ( error ) {
		// handle
	}
};

// const setTemplateModules = async dispatch => {
// 	try {
// 		const templateModules = await getTemplateModules( controller.signal );

// SET_TEMPLATE_MODULES( dispatch, controller.signal )( templateModules );
// 	} catch ( error ) {
// 		// handle
// 	}
// };

const setTemplateModuleTags = async dispatch => {
	try {
		const templateModuleTags = await getTemplateModuleTags( controller.signal );

		SET_TEMPLATE_MODULE_TAGS( dispatch, controller.signal )( templateModuleTags );
	} catch ( error ) {
		// handle
	}
};

const setTemplateOccurrences = async dispatch => {
	try {
		const [ { types } ] = await getTemplateOccurrences( controller.signal );

		const mappedTypes = types.map( ( { id, name } ) => ( {
			key: id,
			text: name,
			value: id
		} ) );

		SET_TEMPLATE_OCCURRENCES( dispatch, controller.signal )( mappedTypes );
	} catch ( error ) {
		// handle
	}
};

const setTemplateRequirements = async dispatch => {
	try {
		const [ { types } ] = await getTemplateRequirements( controller.signal );

		const mappedRequirements = types.map( ( { id, name } ) => ( {
			key: id,
			text: name,
			value: id
		} ) );

		SET_TEMPLATE_REQUIREMENTS( dispatch, controller.signal )( mappedRequirements );
	} catch ( error ) {
		// handle
	}
};

const setTemplateStatuses = async dispatch => {
	try {
		const [ { types } ] = await getTemplateStatuses( controller.signal );

		const mappedStatuses = types.map( ( { id, name } ) => ( {
			key: id,
			text: name,
			value: id
		} ) );

		SET_TEMPLATE_STATUSES( dispatch, controller.signal )( mappedStatuses );
	} catch ( error ) {
		// handle
	}
};

const setTemplateTypes = async dispatch => {
	try {
		const [ { types } ] = await getTemplateTypes( controller.signal );

		const mappedTypes = types.map( ( { description, id, name } ) => ( {
			key: id,
			description,
			text: name,
			value: id
		} ) );

		SET_TEMPLATE_TYPES( dispatch, controller.signal )( mappedTypes );
	} catch ( error ) {
		// handle
	}
};

const onInit = async dispatch => {
	controller = new AbortController();

	await setProgramStatuses( dispatch );
	await setProgramTypes( dispatch );
	// await setTemplateModules( dispatch );
	await setTemplateModuleTags( dispatch );
	await setTemplateOccurrences( dispatch );
	await setTemplateRequirements( dispatch );
	await setTemplateStatuses( dispatch );
	await setTemplateTypes( dispatch );
};

const onCleanup = () => {
	controller.abort();
};

export const {
	StateContext,
	ActionContext,
	Provider
} = createDataContext( {
	reducer: programsReducer,
	initialState,
	controller,
	onInit,
	onCleanup
} );

export function useProgramState() {
	const state = useContext( StateContext );

	return state;
}

export function useProgramAction() {
	const dispatch = useContext( ActionContext );

	const clearTemplate = useCallback( () => {
		SET_TEMPLATE( dispatch, controller.signal )( null );
	}, [ dispatch ] );

	const clearProgram = useCallback( () => {
		SET_PROGRAM( dispatch, controller.signal )( null );
	}, [ dispatch ] );

	const resetSubmissionsTableState = useCallback( () => {
		RESET_SUBMISSIONS_TABLE_STATE( dispatch, controller.signal )();
	}, [ dispatch ] );

	const setCoordination = useCallback( data => {
		SET_COORDINATION( dispatch, controller.signal )( data );
	}, [ dispatch ] );

	const setCoordinationOptions = useCallback( async programID => {
		try {
			const [ { types } ] = await getProgramCoordinationOptions( programID, controller.signal );

			const mappedCoordinationOptions = types.map( ( { id, name } ) => ( {
				key: id,
				text: name,
				value: id
			} ) );

			SET_COORDINATION_OPTIONS( dispatch, controller.signal )( mappedCoordinationOptions );
		} catch ( error ) {
			// handle
		}
	}, [ dispatch ] );

	const setParticipants = useCallback( data => {
		SET_PARTICIPANTS( dispatch, controller.signal )( data );
	}, [ dispatch ] );

	const setProgram = useCallback( data => {
		SET_PROGRAM( dispatch, controller.signal )( data );
	}, [ dispatch ] );

	const setProgramList = useCallback( data => {
		SET_PROGRAM_LIST( dispatch, controller.signal )( data );
	}, [ dispatch ] );

	const setSubmissions = useCallback( data => {
		SET_SUBMISSIONS( dispatch, controller.signal )( data );
	}, [ dispatch ] );

	const setSubmissionsTableState = useCallback( updateFunc => {
		SET_SUBMISSIONS_TABLE_STATE( dispatch, controller.signal )( updateFunc );
	}, [ dispatch ] );

	const setTableState = useCallback( updateFunc => {
		SET_TABLE_STATE( dispatch, controller.signal )( updateFunc );
	}, [ dispatch ] );

	const setTemplate = useCallback( data => {
		SET_TEMPLATE( dispatch, controller.signal )( data );
	}, [ dispatch ] );

	const setTemplates = useCallback( data => {
		SET_TEMPLATES( dispatch, controller.signal )( data );
	}, [ dispatch ] );

	const setTemplateModules = useCallback( async () => {
		try {
			const templateModules = await getTemplateModules( controller.signal );

			SET_TEMPLATE_MODULES( dispatch, controller.signal )( templateModules );
		} catch ( error ) {
			// handle
		}
	}, [ dispatch ] );

	const setTemplateModuleFilters = useCallback( data => {
		SET_TEMPLATE_MODULE_FILTERS( dispatch, controller.signal )( data );
	}, [ dispatch ] );

	return {
		clearTemplate,
		clearProgram,
		setParticipants,
		setProgramList,
		setProgram,
		setSubmissions,
		setSubmissionsTableState,
		setTableState,
		resetSubmissionsTableState,
		setCoordination,
		setCoordinationOptions,
		setTemplate,
		setTemplates,
		setTemplateModules,
		setTemplateModuleFilters
	};
}
